import { useCallback, useEffect, useRef, useState } from "react";
import { ExclamationCircleFilled } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import DeleteConfirmModal from "../DeleteConfirmModal";

interface Options {
  open: boolean;
  // Всичко, което модалът може да промени, като низ (обикновено JSON).
  snapshot: string;
  // Докато се записва, затварянето не пита — записът сам затваря модала.
  isSubmitting?: boolean;
  onClose: () => void;
}

/**
 * Пазач за незапазени промени в модалите на Конфигурация. Модалът вика
 * `markPristine()` в ефекта, с който попълва полетата при отваряне — снимката
 * СЛЕД това попълване е „чистото" състояние. Всички пътища за затваряне
 * (X, „Отказ", Esc, клик извън модала) минават през `requestClose`: при
 * разлика от чистото състояние се пита, иначе се затваря направо.
 * Пазачът поема и Esc, затова модалът не държи собствен Esc listener, а
 * antd `Modal` е с `keyboard={false}`.
 */
export const useUnsavedChangesGuard = ({ open, snapshot, isSubmitting = false, onClose }: Options) => {
  const { t } = useTranslation();
  const [pristineRequest, setPristineRequest] = useState(0);
  const [baseline, setBaseline] = useState<string | null>(null);
  const [isAsking, setIsAsking] = useState(false);
  const latestSnapshotRef = useRef(snapshot);

  useEffect(() => {
    latestSnapshotRef.current = snapshot;
  });

  // Рендерът след markPristine() вече носи попълнените полета — неговата
  // снимка е основата.
  useEffect(() => {
    if (pristineRequest > 0) setBaseline(latestSnapshotRef.current);
  }, [pristineRequest]);

  useEffect(() => {
    if (open) return;
    setBaseline(null);
    setIsAsking(false);
  }, [open]);

  const markPristine = useCallback(() => setPristineRequest((request) => request + 1), []);

  const isDirty = baseline !== null && snapshot !== baseline;

  const requestClose = () => {
    if (isDirty && !isSubmitting) setIsAsking(true);
    else onClose();
  };
  const requestCloseRef = useRef(requestClose);
  useEffect(() => {
    requestCloseRef.current = requestClose;
  });

  useEffect(() => {
    // Докато питаме, Esc е на диалога — той затваря само себе си.
    if (!open || isAsking) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      // preventDefault: без него същото натискане минава и през глобалния
      // Esc-„назад" на SharedAccountGate (правило от CLAUDE.md)
      event.preventDefault();
      requestCloseRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, isAsking]);

  const unsavedDialog = (
    <DeleteConfirmModal
      open={isAsking}
      title={t("common.unsavedTitle")}
      description={t("common.unsavedDesc")}
      confirmLabel={t("common.unsavedLeave")}
      cancelLabel={t("common.unsavedStay")}
      variant="primary"
      icon={<ExclamationCircleFilled className="text-[18px]" />}
      onConfirm={() => {
        setIsAsking(false);
        onClose();
      }}
      onCancel={() => setIsAsking(false)}
    />
  );

  return { requestClose, markPristine, unsavedDialog };
};
